import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";
import { useTheme } from "../context/ThemeContext";
import { Song } from "../types/song";
import { LISTEN_URL, ARTIST_NAME } from "../config/site";
import SongLanding from "../components/SongLanding/SongLanding";

type FetchState = "loading" | "success" | "empty" | "error";

const Listen: React.FC = () => {
  const { currentTheme } = useTheme();
  const [song, setSong] = useState<Song | null>(null);
  const [state, setState] = useState<FetchState>("loading");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchFeaturedSong = async () => {
      setState("loading");
      try {
        const response = await fetch("/api/songs");
        if (response.status === 404) {
          if (!cancelled) setState("empty");
          return;
        }
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
        const data = await response.json();
        if (!cancelled) {
          setSong(data.song);
          setState("success");
        }
      } catch (err) {
        console.error("Error fetching featured song:", err);
        if (!cancelled) setState("error");
      }
    };

    fetchFeaturedSong();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <div className={`bg-gradient-to-br ${currentTheme.colors.primary} min-h-[70vh] flex items-center justify-center px-6`}>
            <div className="flex flex-col items-center gap-6 animate-pulse">
              <div className="w-64 h-64 sm:w-80 sm:h-80 rounded-2xl bg-white/10" />
              <div className="w-48 h-8 rounded bg-white/10" />
              <div className="w-32 h-5 rounded bg-white/10" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (state === "empty" || state === "error") {
    return (
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>Listen | {ARTIST_NAME}</title>
          <meta name="description" content={`Listen to original music by ${ARTIST_NAME}.`} />
          <link rel="canonical" href={LISTEN_URL} />
        </Helmet>
        <Navbar />
        <main className="flex-grow">
          <div className={`bg-gradient-to-br ${currentTheme.colors.primary} min-h-[70vh] flex items-center justify-center px-6`}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center text-white max-w-md"
            >
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {state === "empty" ? "New music coming soon" : "Something went wrong"}
              </h1>
              <p className="text-purple-200 mb-8">
                {state === "empty"
                  ? "A new song hasn't been featured here yet — check back soon, or explore the full catalog."
                  : "We couldn't load this song right now. Please try again."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {state === "error" && (
                  <button
                    type="button"
                    onClick={() => setReloadKey((k) => k + 1)}
                    className="bg-white/10 backdrop-blur-md border border-white/20 text-white font-semibold py-3 px-8 rounded-full min-h-[56px]"
                  >
                    Retry
                  </button>
                )}
                <Link
                  to="/music"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-8 rounded-full min-h-[56px] flex items-center justify-center"
                >
                  Explore Music
                </Link>
              </div>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const currentSong = song as Song;
  const description =
    currentSong.shortDescription || `Listen to ${currentSong.title}, an original song by ${ARTIST_NAME}.`;
  const pageTitle = `${currentSong.title} — ${ARTIST_NAME} | Listen Now`;

  const musicRecordingSchema = {
    "@context": "https://schema.org",
    "@type": "MusicRecording",
    name: currentSong.title,
    byArtist: { "@type": "Person", name: ARTIST_NAME },
    ...(currentSong.coverImage ? { image: currentSong.coverImage } : {}),
    ...(currentSong.releaseDate ? { datePublished: currentSong.releaseDate } : {}),
    url: LISTEN_URL,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta property="og:type" content="music.song" />
        <meta property="og:title" content={`${currentSong.title} — ${ARTIST_NAME}`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={LISTEN_URL} />
        {currentSong.coverImage && (
          <>
            <meta property="og:image" content={currentSong.coverImage} />
            <meta property="og:image:secure_url" content={currentSong.coverImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="1200" />
          </>
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${currentSong.title} — ${ARTIST_NAME}`} />
        <meta name="twitter:description" content={description} />
        {currentSong.coverImage && <meta name="twitter:image" content={currentSong.coverImage} />}
        <link rel="canonical" href={LISTEN_URL} />
        <script type="application/ld+json">{JSON.stringify(musicRecordingSchema)}</script>
      </Helmet>

      <Navbar />
      <main className="flex-grow">
        <SongLanding song={currentSong} />
      </main>
      <Footer />
    </div>
  );
};

export default Listen;
